import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  InputRef,
  Select,
  Space,
  Table,
  TreeSelect
} from 'antd';
import type { TableProps } from 'antd/es/table';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import type {
  ColumnType,
  ColumnsType,
  FilterConfirmProps,
  FilterValue,
  TablePaginationConfig,
  SorterResult
} from 'antd/es/table/interface';
import { useMemo, useRef, useState, useEffect } from 'react';
import { Product } from '../../../models/Product/Product';
import { Category } from '../../../models/Category/Category';
import { StorageRoom } from '../../../models/Storage/StorageRoom';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import style from './product-list.module.scss';
import { Link } from 'react-router-dom';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { currencyFormat } from '../../../utils/helpers/helpers';
import Highlighter from 'react-highlight-words';

// Interface for the data structure displayed in the table
interface ProductTableList {
  key: number;
  id: number;
  name: string;
  category: string;
  stand: string;
  sku: string;
  price: number;
}

type DataIndex = keyof ProductTableList;

export default function ProductList() {
  const searchInput = useRef<InputRef>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const queryClient = useQueryClient();

  // State to hold all table parameters (pagination, filters, search)
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10
    },
    filters: {} as Record<string, FilterValue | null>
  });

  // --- REFACTORED: Separate state for immediate search input ---
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the main search input to avoid excessive API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInputValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputValue]);

  // Fetch data for category and location filters
  const { data: categories = [] } = useQuery(
    ['all-categories-with-children'],
    () => Category.all()
  );
  const { data: storageRooms = [] } = useQuery(['all-storage-rooms'], () =>
    StorageRoom.all()
  );

  // Main query to fetch product data from the server
  const { data: productsResponse, isLoading } = useQuery(
    // --- UPDATED: Query key now correctly depends on the debounced value ---
    [
      'admin-products',
      tableParams.pagination,
      tableParams.filters,
      debouncedSearch
    ],
    () =>
      Product.all({
        isAdmin: true,
        page: tableParams.pagination.current,
        page_size: tableParams.pagination.pageSize,
        search: debouncedSearch,
        // Pass specific column filters to the API
        sku: tableParams.filters.sku?.[0],
        name: tableParams.filters.name?.[0],
        categories: (tableParams.filters.category as number[] | null)?.join(
          ','
        ),
        stands: (tableParams.filters.stand as number[] | null)?.join(',')
      }),
    { keepPreviousData: true }
  );

  // Memoize the formatted data for the table
  const ProductsData: ProductTableList[] = useMemo(
    () =>
      productsResponse?.data?.map((product) => ({
        key: product.id,
        id: product.id,
        name: product.name,
        category: product.category?.name || '—',
        stand: product.storageStand?.name || '—',
        sku: product.sku,
        price: product.price
      })) || [],
    [productsResponse]
  );

  // Function to handle product deletion
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await Product.delete(deleteTarget.id);
      toast.success(`Proizvod "${deleteTarget.name}" je uspešno obrisan.`);
      queryClient.invalidateQueries(['admin-products']); // Refetch data after delete
    } catch (error) {
      toast.error('Došlo je do greške prilikom brisanja.');
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // Function to update state when table changes (pagination, filters, etc.)
  const handleTableChange: TableProps<ProductTableList>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination: {
        current: pagination.current ?? 1,
        pageSize: pagination.pageSize ?? 10
      },
      filters
    });
  };

  // Function to generate search filter UI for text-based columns
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<ProductTableList> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div className="cs-table-dropdown" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži ${dataIndex}`}
          value={selectedKeys ? selectedKeys[0] : ''}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
        />
        <div className="cs-table-dropdown-actions">
          <button onClick={() => confirm()} className="cs-table-button-primary">
            Pretraži
          </button>
          <button
            onClick={() => clearFilters && clearFilters()}
            className="cs-table-button-secondary"
          >
            Resetuj
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    filteredValue: tableParams.filters[dataIndex] || null,
    render: (text) => {
      const searchWord = tableParams.filters[dataIndex]?.[0] as
        | string
        | undefined;
      return searchWord ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchWord]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      );
    }
  });

  // Define the columns for the Ant Design table
  const columns: ColumnsType<ProductTableList> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      ...getColumnSearchProps('sku')
    },
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      render: (name, record) => (
        <Link to={`/admin/product/${record.id}`}>{name}</Link>
      )
    },
    {
      title: 'Kategorija',
      dataIndex: 'category',
      key: 'category',
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filteredValue: tableParams.filters.category || null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }) => (
        <div
          className="cs-table-dropdown cs-table-dropdown-nested"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TreeSelect
            className={style.filterTreeSelect}
            value={selectedKeys as number[]}
            treeData={categories}
            multiple
            treeDefaultExpandAll
            placeholder="Izaberi kategoriju"
            onChange={(value) => setSelectedKeys(value)}
            treeNodeFilterProp="title"
            showSearch
          />
          <div className="cs-table-dropdown-actions">
            <button
              onClick={() => confirm()}
              className="cs-table-button-primary"
            >
              Filter
            </button>
            <button
              onClick={() => clearFilters && clearFilters()}
              className="cs-table-button-secondary"
            >
              Reset
            </button>
          </div>
        </div>
      )
    },
    {
      title: 'Lokacija',
      dataIndex: 'stand',
      key: 'stand',
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filteredValue: tableParams.filters.stand || null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }) => (
        <div
          className="cs-table-dropdown cs-table-dropdown-nested"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Select
            mode="multiple"
            allowClear
            className={style.filterSelect}
            placeholder="Izaberi lokaciju"
            value={selectedKeys as number[]}
            onChange={(value) => setSelectedKeys(value)}
          >
            {storageRooms.map((room) => (
              <Select.OptGroup label={room.name} key={room.id}>
                {room.stands.map((stand) => (
                  <Select.Option value={stand.id} key={stand.id}>
                    {stand.name}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
          <div className="cs-table-dropdown-actions">
            <button
              onClick={() => confirm()}
              className="cs-table-button-primary"
            >
              Filter
            </button>
            <button
              onClick={() => clearFilters && clearFilters()}
              className="cs-table-button-secondary"
            >
              Reset
            </button>
          </div>
        </div>
      )
    },
    {
      title: 'Cena (RSD)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (price) => currencyFormat(price)
    },
    {
      title: 'Akcije',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/admin/products/edit/${record.id}`}>
            <EditIcon />
          </Link>
          <button
            className={style.deleteButton}
            onClick={() => {
              setDeleteTarget({ id: record.id, name: record.name });
              setShowDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </button>
        </Space>
      )
    }
  ];

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista Proizvoda</h1>
      <div className={style.info}>
        <div className={style.inputSearchWrapper}>
          <Input
            placeholder="Opšta pretraga..."
            prefix={<SearchOutlined />}
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            style={{ width: 300 }}
          />
        </div>
        <ButtonPrimary url="/admin/products/create">
          Dodaj Proizvod
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={ProductsData}
        rowKey="id"
        loading={isLoading}
        pagination={{
          ...tableParams.pagination,
          total: productsResponse?.pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100']
        }}
        onChange={handleTableChange}
        className="cs-table-wrapper"
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete proizvod "${deleteTarget.name}"?`
            : ''
        }
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
